import { useEffect, useRef, useState } from "react";
import AccountButton from "../AccountButton";
import AccountSection from "../AccountSection";
import {
  addMedication,
  deleteMedication,
  getMedications,
  updateMedication,
} from "../api/apiFunctions";
import { DisplayOnCardPill } from "../components/Pills";
import { t } from "../i18n";
import { getAuthToken } from "../utils/auth";
import { replaceNullWithPlaceholder } from "../utils";

export default function MedicationsPage({ lang = "en", goToTab }) {
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    medication: "",
    dosage: "",
    details: "",
  });
  const [medications, setMedications] = useState([]);
  const [tempMedication, setTempMedication] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    dosage: "",
    status: "active",
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [medicationToDelete, setMedicationToDelete] = useState(null);
  const [showDosageModal, setShowDosageModal] = useState(false);
  const [medicationToAdd, setMedicationToAdd] = useState("");

  // RxTerms API search states
  const [medicationSearch, setMedicationSearch] = useState("");
  const [medicationResults, setMedicationResults] = useState([]);
  const [showMedicationDropdown, setShowMedicationDropdown] = useState(false);
  const [isMedicationLoading, setIsMedicationLoading] = useState(false);
  const medicationDropdownRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load medications on component mount
  useEffect(() => {
    const loadMedications = async () => {
      try {
        setIsLoading(true);
        setError("");

        const token = getAuthToken();
        if (!token) {
          throw new Error(
            "Authentication token not found. Please login again.",
          );
        }

        const response = await getMedications(token);

        const extractMedicationsData = (response) => {
          try {
            // Case 1: Response is already an array
            if (Array.isArray(response)) {
              return response;
            }

            // Case 2: Response has success property and data.medications array (API structure)
            if (
              response &&
              typeof response === "object" &&
              response.success &&
              response.data
            ) {
              if (
                response.data.medications &&
                Array.isArray(response.data.medications)
              ) {
                return response.data.medications;
              } else if (Array.isArray(response.data)) {
                return response.data;
              } else {
                return [];
              }
            }

            // Case 3: Response has data property (without success)
            if (response && typeof response === "object" && response.data) {
              if (
                response.data.medications &&
                Array.isArray(response.data.medications)
              ) {
                return response.data.medications;
              } else if (Array.isArray(response.data)) {
                return response.data;
              } else {
                return [];
              }
            }

            // Case 4: Response is an object but not the expected format
            if (response && typeof response === "object") {
              return [];
            }

            // Case 5: No valid data found
            return [];
          } catch (error) {
            console.error("Error extracting medications data:", error);
            return [];
          }
        };

        // Extract medications data safely
        const medicationsData = extractMedicationsData(response);

        try {
          if (Array.isArray(medicationsData)) {
            if (medicationsData.length > 0) {
              const transformedMedications = medicationsData.map(
                (med, index) => {
                  try {
                    return {
                      id: med.id || "",
                      name:
                        med.medicineName || med.name || "Unknown Medication",
                      dosage: med.dosage || "",
                      status: med.status || "active",
                      displayOnCard: med.displayOnCard,
                    };
                  } catch (medError) {
                    console.error(
                      "Error processing individual medication:",
                      medError,
                      med,
                    );
                    return {
                      id: `error_${Date.now()}_${index}`,
                      name: "Error loading medication",
                      dosage: "",
                      status: "active",
                    };
                  }
                },
              );
              setMedications(transformedMedications);
            } else {
              setMedications([]);
            }
          } else {
            setMedications([]);
          }
        } catch (transformError) {
          console.error("Error transforming medications data:", transformError);
          setMedications([]);
        }
      } catch (error) {
        console.error("Error loading medications:", error);
        setError(
          error.message || "Failed to load medications. Please try again.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadMedications();
  }, []);

  // Handle clicks outside medication dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        medicationDropdownRef.current &&
        !medicationDropdownRef.current.contains(event.target)
      ) {
        setShowMedicationDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch medications from RxTerms API
  useEffect(() => {
    const fetchMedications = async () => {
      if (!medicationSearch || medicationSearch.length < 2) {
        setMedicationResults([]);
        setIsMedicationLoading(false);
        return;
      }

      setIsMedicationLoading(true);
      try {
        const url = `https://clinicaltables.nlm.nih.gov/api/rxterms/v3/search?terms=${encodeURIComponent(
          medicationSearch,
        )}&count=5&df=DISPLAY_NAME&ef=STRENGTHS_AND_FORMS,RXCUIS`;
        const res = await fetch(url);
        const data = await res.json();

        // Parse the API response according to the documentation
        // data[3] contains the display fields (DISPLAY_NAME)
        // data[2] contains the extra fields (STRENGTHS_AND_FORMS, RXCUIS)
        const results = (data[3] || []).map((row, idx) => ({
          displayName: row[0] || "",
          strengthsAndForms: data[2]?.STRENGTHS_AND_FORMS?.[idx] || [],
          rxcuis: data[2]?.RXCUIS?.[idx] || [],
        }));

        setMedicationResults(results);
      } catch (e) {
        console.error("Error fetching medications:", e);
        setMedicationResults([]);
        setShowMedicationDropdown(true);
      } finally {
        setIsMedicationLoading(false);
      }
    };
    const timeout = setTimeout(fetchMedications, 400);
    return () => clearTimeout(timeout);
  }, [medicationSearch]);

  // Dropdown options
  const dosageOptions = [
    { value: "", label: t("selectDosage", lang) },
    { value: "0.5mg", label: "0.5mg" },
    { value: "1mg", label: "1mg" },
    { value: "2.5mg", label: "2.5mg" },
    { value: "5mg", label: "5mg" },
    { value: "10mg", label: "10mg" },
    { value: "15mg", label: "15mg" },
    { value: "20mg", label: "20mg" },
    { value: "25mg", label: "25mg" },
    { value: "30mg", label: "30mg" },
    { value: "40mg", label: "40mg" },
    { value: "50mg", label: "50mg" },
    { value: "60mg", label: "60mg" },
    { value: "75mg", label: "75mg" },
    { value: "80mg", label: "80mg" },
    { value: "100mg", label: "100mg" },
    { value: "125mg", label: "125mg" },
    { value: "150mg", label: "150mg" },
    { value: "200mg", label: "200mg" },
    { value: "250mg", label: "250mg" },
    { value: "300mg", label: "300mg" },
    { value: "400mg", label: "400mg" },
    { value: "500mg", label: "500mg" },
    { value: "600mg", label: "600mg" },
    { value: "750mg", label: "750mg" },
    { value: "800mg", label: "800mg" },
    { value: "1000mg", label: "1000mg" },
    { value: "0.5ml", label: "0.5ml" },
    { value: "1ml", label: "1ml" },
    { value: "2ml", label: "2ml" },
    { value: "5ml", label: "5ml" },
    { value: "10ml", label: "10ml" },
    { value: "15ml", label: "15ml" },
    { value: "20ml", label: "20ml" },
    { value: "25ml", label: "25ml" },
    { value: "30ml", label: "30ml" },
    { value: "50ml", label: "50ml" },
    { value: "100ml", label: "100ml" },
    { value: "250ml", label: "250ml" },
    { value: "500ml", label: "500ml" },
    { value: "1000ml", label: "1000ml" },
    { value: "1mcg", label: "1mcg" },
    { value: "5mcg", label: "5mcg" },
    { value: "10mcg", label: "10mcg" },
    { value: "25mcg", label: "25mcg" },
    { value: "50mcg", label: "50mcg" },
    { value: "100mcg", label: "100mcg" },
    { value: "250mcg", label: "250mcg" },
    { value: "500mcg", label: "500mcg" },
    { value: "1000mcg", label: "1000mcg" },
    { value: "1 unit", label: "1 unit" },
    { value: "2 units", label: "2 units" },
    { value: "5 units", label: "5 units" },
    { value: "10 units", label: "10 units" },
    { value: "15 units", label: "15 units" },
    { value: "20 units", label: "20 units" },
    { value: "25 units", label: "25 units" },
    { value: "30 units", label: "30 units" },
    { value: "40 units", label: "40 units" },
    { value: "50 units", label: "50 units" },
    { value: "75 units", label: "75 units" },
    { value: "100 units", label: "100 units" },
    { value: "other", label: t("other", lang) },
  ];
  const statusOptions = [
    { value: "all", label: t("allStatuses", lang) },
    { value: "active", label: t("active", lang) },
    { value: "inactive", label: t("inactive", lang) },
  ];

  // Handlers
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleStatusChange = (e) => setStatusFilter(e.target.value);
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleMedicationSearchChange = (e) => {
    const value = e.target.value;
    setMedicationSearch(value);
    setForm((prev) => ({ ...prev, medication: value }));
    if (value.length >= 2) {
      setShowMedicationDropdown(true);
    } else {
      setShowMedicationDropdown(false);
    }
  };

  const handleMedicationSelect = (medication) => {
    setForm((prev) => ({
      ...prev,
      medication: medication.displayName,
      dosage:
        medication.strengthsAndForms.length > 0
          ? medication.strengthsAndForms[0]
          : "",
    }));
    setMedicationSearch(medication.displayName);
    setShowMedicationDropdown(false);
  };

  const saveMedication = async () => {
    // Check both manual entry and search fields
    const medicationName = tempMedication.trim() || form.medication.trim();

    if (!medicationName) {
      setError("Please enter a medication name");
      setTimeout(() => {
        setError("");
      }, 3000);
      return false;
    }

    if (!form.dosage) {
      setError("Please select a dosage");
      setTimeout(() => {
        setError("");
      }, 3000);
      return false;
    }

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const medicationData = {
        medicineName: medicationName,
        dosage: form.dosage,
        status: "active",
      };

      const response = await addMedication(medicationData, token);

      if (response.success) {
        // Add the new medication to the local state
        const newMedication = {
          id: response.data.medicationId || "",
          name: medicationName,
          dosage: form.dosage,
          status: "active",
        };
        setMedications((prev) => [...prev, newMedication]);

        // Clear the form
        setForm({ medication: "", dosage: "", details: "" });
        setMedicationSearch("");
        setTempMedication("");
        setSuccessMessage("Medication added successfully!");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
        return true; // Return true to indicate success
      } else {
        throw new Error(response.message || "Failed to add medication");
      }
    } catch (error) {
      console.error("Error adding medication:", error);
      setError(error.message || "Failed to add medication. Please try again.");
      return false; // Return false to indicate failure
    } finally {
      setIsSaving(false);
      setTimeout(() => {
        setError("");
      }, 3000);
    }
  };

  const addMedicationLocal = () => {
    if (tempMedication.trim()) {
      // Copy to main form and use existing dosage field
      setForm((prev) => ({ ...prev, medication: tempMedication.trim() }));
      // setTempMedication(tempMedication);
    }
  };

  const confirmAddMedication = async (dosage) => {
    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const medicationData = {
        medicineName: medicationToAdd,
        dosage: dosage,
        status: "active",
      };

      const response = await addMedication(medicationData, token);

      if (response.success) {
        // Add the new medication to the local state
        const newMedication = {
          id: response.data._id || response.data.id,
          name: medicationToAdd,
          dosage: dosage,
          status: "active",
        };
        setMedications((prev) => [...prev, newMedication]);

        setTempMedication("");
        setMedicationToAdd("");
        setShowDosageModal(false);
        setSuccessMessage("Medication added successfully!");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        throw new Error(response.message || "Failed to add medication");
      }
    } catch (error) {
      console.error("Error adding medication:", error);
      setError(error.message || "Failed to add medication. Please try again.");
    } finally {
      setIsSaving(false);
      setTimeout(() => {
        setError("");
      }, 3000);
    }
  };

  const removeMedication = (index) => {
    const medication = medications[index];
    setMedicationToDelete({ index, medication });
    setShowDeleteConfirm(true);
  };

  const confirmDeleteMedication = async () => {
    if (!medicationToDelete) return;

    const { index, medication } = medicationToDelete;

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const response = await deleteMedication(medication.id, token);

      if (response.success) {
        setMedications((prev) => prev.filter((_, i) => i !== index));
        setSuccessMessage("Medication deleted successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        throw new Error(response.message || "Failed to delete medication");
      }
    } catch (error) {
      console.error("Error deleting medication:", error);
      setError(
        error.message || "Failed to delete medication. Please try again.",
      );
    } finally {
      setIsSaving(false);
      setShowDeleteConfirm(false);
      setMedicationToDelete(null);
      setTimeout(() => {
        setError("");
      }, 3000);
    }
  };

  const cancelDeleteMedication = () => {
    setShowDeleteConfirm(false);
    setMedicationToDelete(null);
  };

  const startEdit = (index) => {
    const medication = medications[index];
    setEditingIndex(index);
    setEditForm({
      name: medication.name,
      dosage: medication.dosage,
      status: medication.status,
    });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditForm({ name: "", dosage: "", status: "active" });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) {
      setError("Please enter a medication name");
      setTimeout(() => {
        setError("");
      }, 3000);
      return;
    }

    const medication = medications[editingIndex];

    if (!medication.id) {
      throw new Error("Try refreshing the page.");
    }

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const medicationData = {
        medicineName: editForm.name.trim(),
        dosage: editForm.dosage,
        status: editForm.status,
      };

      const response = await updateMedication(
        medication.id,
        medicationData,
        token,
      );

      if (response.success) {
        // Update the medication in local state
        setMedications((prev) =>
          prev.map((med, index) =>
            index === editingIndex
              ? {
                  ...med,
                  name: editForm.name.trim(),
                  dosage: editForm.dosage,
                  status: editForm.status,
                }
              : med,
          ),
        );
        cancelEdit();
        setSuccessMessage("Medication updated successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        throw new Error(response.message || "Failed to update medication");
      }
    } catch (error) {
      console.error("Error updating medication:", error);
      setError(
        error.message || "Failed to update medication. Please try again.",
      );
      setTimeout(() => setError(""), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center p-2">
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
      <span className="ml-2 text-xs text-[#837f90]">Searching...</span>
    </div>
  );

  // No results component
  const NoResultsMessage = ({ searchTerm }) => (
    <div className="p-3 text-center">
      <div className="text-xs text-[#837f90] mb-1">
        No medications found for "{searchTerm}"
      </div>
      <div className="text-xs text-[#837f90] opacity-75">
        Try a different search term or check spelling
      </div>
    </div>
  );

  // Filtered medications
  const filteredMedications = medications.filter((med) => {
    const matchesSearch = med.name
      ?.toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || med.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const showInfoColumn = filteredMedications.some((med) => med.displayOnCard);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f7f8fa] flex flex-col items-center pb-8 max-w-4xl mx-auto">
      <div className="w-full max-w-4xl px-4 md:px-0">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          {/* Medicine & Supplements */}
          <AccountSection>{t("medicineSupplements", lang)}</AccountSection>

          {/* Error and Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {successMessage}
            </div>
          )}

          <div className="space-y-4 mb-6">
            {/* Manual medication entry */}
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("addMedication", lang)}
              </label>
              <div className="flex gap-2">
                <input
                  value={tempMedication}
                  onChange={(e) => setTempMedication(e.target.value)}
                  placeholder={
                    t("addMedication", lang) || "Add medication manually..."
                  }
                  className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    (e.preventDefault(), addMedicationLocal())
                  }
                />
              </div>
            </div>

            {/* Search medications */}
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("searchMedications", lang)}
              </label>
              <div className="relative" ref={medicationDropdownRef}>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    name="medication"
                    value={medicationSearch}
                    onChange={handleMedicationSearchChange}
                    placeholder={t("searchMedicationPlaceholder", lang)}
                    className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                  />
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded bg-[#f7f8fa] border border-gray-200">
                    {isMedicationLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        fill="none"
                        stroke="#837f90"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                      </svg>
                    )}
                  </span>
                </div>
                {showMedicationDropdown && (
                  <div className="absolute left-0 top-12 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-64 overflow-y-auto">
                    {isMedicationLoading ? (
                      <LoadingSpinner />
                    ) : medicationResults.length > 0 ? (
                      medicationResults.map((medication, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-[#f7f8fa] border-b border-gray-100 last:border-b-0"
                          onClick={() => handleMedicationSelect(medication)}
                        >
                          <div className="font-semibold text-[#2d348f]">
                            {medication.displayName}
                          </div>
                          {medication.strengthsAndForms.length > 0 && (
                            <div className="text-xs text-[#837f90]">
                              Available:{" "}
                              {medication.strengthsAndForms
                                .slice(0, 3)
                                .join(", ")}
                              {medication.strengthsAndForms.length > 3 && "..."}
                            </div>
                          )}
                        </button>
                      ))
                    ) : medicationSearch.length >= 2 ? (
                      <NoResultsMessage searchTerm={medicationSearch} />
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            {/* Dosage selection */}
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("dosage", lang)}
              </label>
              <select
                name="dosage"
                value={form.dosage}
                onChange={handleFormChange}
                className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
              >
                {dosageOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <AccountButton
              className="md:w-1/3"
              disabled={isSaving}
              onClick={() => goToTab("prev")}
            >
              <span className="mr-2">&laquo;</span> {t("back", lang)}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => saveMedication()}
              disabled={isSaving}
            >
              {isSaving ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                t("save", lang)
              )}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => goToTab("next")}
              disabled={isSaving}
            >
              <>
                {t("next", lang)} <span className="ml-2">&raquo;</span>
              </>
            </AccountButton>
          </div>

          {/* My Medications Table */}
          <AccountSection>{t("myMedications", lang)}</AccountSection>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4962d8]"></div>
              <span className="ml-3 text-[#837f90]">
                Loading medications...
              </span>
            </div>
          ) : (
            <>
              <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                <div className="flex-1 flex items-center gap-2">
                  <input
                    id="medication-search"
                    type="text"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder={t("searchPlaceholder", lang)}
                    className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                    style={{ maxWidth: 200 }}
                  />
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-[#f7f8fa] border border-gray-200">
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      stroke="#837f90"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                  </span>
                </div>
                <select
                  value={statusFilter}
                  onChange={handleStatusChange}
                  className="border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                  style={{ maxWidth: 140 }}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="max-w-xs sm:max-w-full overflow-x-auto">
                <table className="bg-white text-sm min-w-full">
                  <thead>
                    <tr className="bg-[#f7f8fa] text-[#837f90]">
                      {showInfoColumn && (
                        <th className="px-4 py-2 text-left font-semibold">
                          {t("Info", lang)}
                        </th>
                      )}
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("medicationName", lang)}
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("dosage", lang)}
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("status", lang)}
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("actions", lang)}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="overflow-x-scroll">
                    {filteredMedications.length === 0 ? (
                      <tr className="overflow-x-scroll">
                        <td
                          colSpan={4}
                          className="text-center py-4 text-[#837f90] overflow-x-scroll"
                        >
                          {medications.length === 0 ? (
                            <div className="flex flex-col items-center">
                              <div className="text-4xl mb-2">💊</div>
                              <div className="text-sm font-medium mb-1">
                                No medications added yet
                              </div>
                              <div className="text-xs opacity-75">
                                Add your first medication using the form above
                              </div>
                            </div>
                          ) : search ? (
                            <div className="flex flex-col items-center">
                              <div className="text-3xl mb-2">🔍</div>
                              <div className="text-sm font-medium mb-1">
                                No medications found for "{search}"
                              </div>
                              <div className="text-xs opacity-75 mb-2">
                                Try a different search term or clear the search
                              </div>
                              <button
                                onClick={() => setSearch("")}
                                className="px-3 py-1 text-xs bg-[#4962d8] text-white rounded hover:bg-[#2d348f] transition-colors"
                              >
                                Clear Search
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <div className="text-3xl mb-2">🔽</div>
                              <div className="text-sm font-medium mb-1">
                                No medications match the current filter
                              </div>
                              <div className="text-xs opacity-75 mb-2">
                                Try changing the status filter or clear the
                                search
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setStatusFilter("all")}
                                  className="px-3 py-1 text-xs bg-[#4962d8] text-white rounded hover:bg-[#2d348f] transition-colors"
                                >
                                  Show All
                                </button>
                                {search && (
                                  <button
                                    onClick={() => setSearch("")}
                                    className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                  >
                                    Clear Search
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : (
                      filteredMedications.map((med, idx) => {
                        const actualIndex = medications.findIndex(
                          (m) => m === med,
                        );
                        const isEditing = editingIndex === actualIndex;

                        return (
                          <tr key={idx} className="border-t border-gray-100">
                            {isEditing ? (
                              <>
                                {showInfoColumn && (
                                  <td className="px-4 py-2">
                                    {med.displayOnCard && <DisplayOnCardPill />}
                                  </td>
                                )}
                                <td className="px-4 py-2">
                                  <input
                                    type="text"
                                    name="name"
                                    value={editForm.name}
                                    onChange={handleEditFormChange}
                                    className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <select
                                    name="dosage"
                                    value={editForm.dosage}
                                    onChange={handleEditFormChange}
                                    className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                                  >
                                    {dosageOptions.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-4 py-2">
                                  <select
                                    name="status"
                                    value={editForm.status}
                                    onChange={handleEditFormChange}
                                    className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                                  >
                                    <option value="active">
                                      {t("active", lang)}
                                    </option>
                                    <option value="inactive">
                                      {t("inactive", lang)}
                                    </option>
                                  </select>
                                </td>
                                <td className="px-4 py-2">
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={saveEdit}
                                      className="text-green-500 hover:text-green-700 p-1 rounded hover:bg-green-50"
                                      title={t("save", lang)}
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        viewBox="0 0 24 24"
                                      >
                                        <polyline points="9,11 12,14 22,4" />
                                        <path d="M21,12v7a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2V5a2,2,0,0,1,2-2h11" />
                                      </svg>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={cancelEdit}
                                      className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-50"
                                      title={t("cancel", lang)}
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        viewBox="0 0 24 24"
                                      >
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                {showInfoColumn && (
                                  <td className="px-4 py-2">
                                    {med.displayOnCard && <DisplayOnCardPill />}
                                  </td>
                                )}
                                <td className="px-4 py-2">
                                  {replaceNullWithPlaceholder(
                                    med.name,
                                    "No Name",
                                  )}
                                </td>
                                <td className="px-4 py-2">
                                  {replaceNullWithPlaceholder(
                                    med.dosage,
                                    "No Dosage",
                                  )}
                                </td>
                                <td className="px-4 py-2">
                                  <span
                                    className={`inline-block px-3 py-1 text-xs rounded-full ${
                                      med.status === "active"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-700"
                                    }`}
                                  >
                                    {t(med.status, lang)}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => startEdit(actualIndex)}
                                      className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
                                      title={t("edit", lang)}
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                      </svg>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeMedication(actualIndex)
                                      }
                                      className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                      title={t("delete", lang)}
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        viewBox="0 0 24 24"
                                      >
                                        <polyline points="3,6 5,6 21,6" />
                                        <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6" />
                                        <line x1="10" y1="11" x2="10" y2="17" />
                                        <line x1="14" y1="11" x2="14" y2="17" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="text-red-500 text-2xl mr-3">⚠️</div>
              <h3 className="text-lg font-semibold text-gray-900">
                Delete Medication
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{" "}
              <strong>"{medicationToDelete?.medication?.name}"</strong>? This
              action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDeleteMedication}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteMedication}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isSaving}
              >
                {isSaving ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dosage Selection Modal */}
      {showDosageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="text-blue-500 text-2xl mr-3">💊</div>
              <h3 className="text-lg font-semibold text-gray-900">
                Add Dosage
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              Please select a dosage for <strong>"{medicationToAdd}"</strong>
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dosage
              </label>
              <select
                id="modal-dosage"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                defaultValue=""
              >
                {dosageOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDosageModal(false);
                  setMedicationToAdd("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const dosageSelect = document.getElementById("modal-dosage");
                  const selectedDosage = dosageSelect ? dosageSelect.value : "";
                  confirmAddMedication(selectedDosage);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-[#4962d8] rounded-md hover:bg-[#2d348f] transition-colors disabled:opacity-50"
                disabled={isSaving}
              >
                {isSaving ? "Adding..." : "Add Medication"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
